import type { AgentActivityEntry } from "@p0security/integrations-agentic-shared/interface";
import { assertNever } from "@p0security/shared/types/index";
import { type CollectExportData, Export } from "components/Export";

import { stringifyJson } from "./EventDetails.tsx";
import { EXPORT_MAX_ENTRIES } from "./hooks/useEventLog.ts";

const EXPORT_COLUMNS = [
  "timestamp",
  "protocol",
  "caller",
  "clientId",
  "actors",
  "mcpServerName",
  "bridgeId",
  "bridgeBaseUrl",
  "agentId",
  "toolName",
  "method",
  "direction",
  "messageKind",
  "allowed",
  "sessionId",
  "requestId",
  "id",
  "traceId",
  "spanId",
  "traceSampled",
  "arguments",
] as const;
type ExportColumn = (typeof EXPORT_COLUMNS)[number];

// Leading characters that spreadsheet apps interpret as a formula prefix;
// `arguments` is often a raw CLI command, so this is a real injection vector
// (e.g. a cell reading `=cmd|...`) rather than a theoretical one.
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

/** Quotes every cell, so embedded commas, quotes, and newlines survive, and
 * neutralizes a leading formula-trigger character so opening the export in
 * Excel/Sheets can't execute a formula from tool-call data. */
const csvCell = (value: unknown) => {
  const text = typeof value === "string" ? value : stringifyJson(value);
  const safeText = CSV_FORMULA_PREFIX.test(text) ? `'${text}` : text;
  return `"${safeText.replace(/"/g, '""')}"`;
};

const exportValue = (entry: AgentActivityEntry, column: ExportColumn) => {
  if (column === "traceId") return entry.trace?.traceId;
  if (column === "spanId") return entry.trace?.spanId;
  if (column === "traceSampled") return entry.trace?.sampled;
  // Every other column reads straight off the entry: the union gives a field
  // its own protocol carries no value for the type `undefined`, so a
  // per-protocol check would only restate what the type already says.
  switch (column) {
    case "actors":
    case "agentId":
    case "allowed":
    case "arguments":
    case "bridgeBaseUrl":
    case "bridgeId":
    case "caller":
    case "clientId":
    case "direction":
    case "id":
    case "mcpServerName":
    case "messageKind":
    case "method":
    case "protocol":
    case "requestId":
    case "sessionId":
    case "timestamp":
    case "toolName":
      return entry[column];
    default:
      return assertNever(column);
  }
};

///////////////////////////////////////////////////////
// TODO: Deduplicate with existing assessment export //
///////////////////////////////////////////////////////

/** Serializes event entries as CSV with scalar trace correlation fields. */
export const toCsv = async (entries: AgentActivityEntry[]) =>
  [
    EXPORT_COLUMNS.join(","),
    ...entries.map((entry) =>
      EXPORT_COLUMNS.map((column) => csvCell(exportValue(entry, column))).join(
        ","
      )
    ),
  ].join("\n");

/** Serializes protocol-discriminated event entries as JSON. */
export const toJson = async (entries: AgentActivityEntry[]) =>
  stringifyJson(entries, 2);

/** Exports the event log calls matching the applied filters and search —
 * not just the pages loaded into the table. `hasLocalSearch` only labels the
 * button; the search itself is applied by the collector. */
export const EventLogExport: React.FC<{
  collect: CollectExportData<AgentActivityEntry>;
  hasLocalSearch: boolean;
}> = ({ collect, hasLocalSearch }) => (
  <Export
    data={collect}
    filename="p0-agentic-event-log"
    help={`narrow the time range or add event filters. An export covers at most ${EXPORT_MAX_ENTRIES.toLocaleString()} calls, most recent first, then keeps those matching your search.`}
    options={{
      csv: { label: "CSV", blob: toCsv, extension: "csv" },
      json: { label: "JSON", blob: toJson, extension: "json" },
    }}
    title={hasLocalSearch ? "Export matches" : "Export"}
  />
);
